import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import i18n from "i18next";
import { useAuth } from "@/lib/auth";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { refreshWorkspace } = useAuth();
    const [status, setStatus] = useState("pending");
    const [data, setData] = useState(null);
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;

        const poll = async (n) => {
            if (cancelled) return;
            try {
                const res = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                setData(res.data);
                if (res.data.payment_status === "paid") {
                    setStatus("paid");
                    refreshWorkspace();
                    return;
                }
                if (res.data.status === "expired" || res.data.payment_status === "failed") {
                    setStatus("failed");
                    return;
                }
                if (n >= 5) {
                    setStatus("timeout");
                    return;
                }
                setAttempts(n + 1);
                setTimeout(() => poll(n + 1), 2000);
            } catch (e) {
                if (!cancelled) setStatus("failed");
            }
        };
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId, refreshWorkspace]);

    return (
        <div className="min-h-screen grid place-items-center px-6">
            <div className="max-w-md w-full text-center rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-10 jr-fadein">
                {status === "pending" && <>
                    <Loader2 className="animate-spin text-[var(--jr-brand)] mx-auto" size={40}/>
                    <h1 className="font-display text-2xl tracking-tight mt-6">{i18n.t("billing.processing")}</h1>
                    <p className="text-[var(--jr-text-2)] mt-2">{i18n.t("billing.attempt", { n: attempts + 1 })}</p>
                </>}
                {status === "paid" && <>
                    <div className="w-14 h-14 rounded-full bg-[var(--jr-success)]/15 grid place-items-center mx-auto"><Check className="text-[var(--jr-success)]" size={28}/></div>
                    <h1 className="font-display text-3xl tracking-tight mt-6">{i18n.t("billing.success")}</h1>
                    <p className="text-[var(--jr-text-2)] mt-2">{data?.credits_granted ? i18n.t("billing.credits_added", { count: data.credits_granted }) : i18n.t("billing.tier_upgraded")}</p>
                    <Link to="/app/dashboard" data-testid="back-dashboard" className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] transition-colors">
                        {i18n.t("billing.to_dashboard")} <ArrowRight size={16}/>
                    </Link>
                </>}
                {(status === "failed" || status === "timeout") && <>
                    <AlertCircle className="text-[var(--jr-error)] mx-auto" size={40}/>
                    <h1 className="font-display text-2xl tracking-tight mt-6">{status === "timeout" ? i18n.t("billing.status_unknown") : i18n.t("billing.failed")}</h1>
                    <p className="text-[var(--jr-text-2)] mt-2">{status === "timeout" ? i18n.t("billing.timeout_msg") : i18n.t("billing.retry_msg")}</p>
                    <Link to="/app/credits" className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)]">
                        {i18n.t("billing.back_credits")} <ArrowRight size={16}/>
                    </Link>
                </>}
            </div>
        </div>
    );
}
